import React, { useState, useRef } from 'react';
import { Select, Button, Table, Tag, Row, Col, message, Progress, Badge, Alert, Tooltip, Space, InputNumber, Divider, Card, DatePicker } from 'antd';
import { UserAddOutlined, ThunderboltOutlined, PauseCircleOutlined, SearchOutlined, CheckCircleOutlined, WarningOutlined, CloseCircleOutlined, MinusCircleOutlined, LinkOutlined, InstagramOutlined, PictureOutlined } from '@ant-design/icons';
import apiClient from '../../server/server';

const EnrichArtistTab = ({ countries = [] }) => {
  // City picker
  const [selectedCountryId, setSelectedCountryId] = useState(null);
  const [selectedCityId, setSelectedCityId] = useState(null);
  const [cities, setCities] = useState([]);

  // Events
  const [events, setEvents] = useState([]);
  const [loadingEvents, setLoadingEvents] = useState(false);

  // Enrich state
  const [enrichResults, setEnrichResults] = useState({}); // { [eventId]: { status, results, summary, event } }
  const [enrichingEventId, setEnrichingEventId] = useState(null);
  const [batchRunning, setBatchRunning] = useState(false);
  const stopRef = useRef(false);

  // Fix broken pictures state
  const [fixingPictures, setFixingPictures] = useState(false);
  const [fixPicturesLimit, setFixPicturesLimit] = useState(50);
  const [fixPicturesSince, setFixPicturesSince] = useState(null);
  const [fixPicturesResult, setFixPicturesResult] = useState(null);

  const getUserId = () => {
    const user = JSON.parse(localStorage.getItem('user'));
    return user?._id || null;
  };

  // ── City Selection ──
  const activeCountries = countries.filter(c => c.cities?.some(city => city.isEnabled));

  const handleCountryChange = (countryId) => {
    setSelectedCountryId(countryId);
    setSelectedCityId(null);
    setEvents([]);
    setEnrichResults({});
    const country = countries.find(c => c._id === countryId);
    setCities((country?.cities || []).filter(city => city.isEnabled));
  };

  const handleCityChange = (cityId) => {
    setSelectedCityId(cityId);
    setEvents([]);
    setEnrichResults({});
  };

  // ── Load Events ──
  const handleLoadEvents = async () => {
    if (!selectedCityId) {
      message.warning('Please select a city');
      return;
    }

    setLoadingEvents(true);
    setEnrichResults({});

    try {
      const response = await apiClient.get('/publications/discovery', {
        params: {
          pagination: true,
          page: 1,
          limit: 100,
          publicationType: 'Event',
          'condition[isPublished]': true,
          'condition[upcoming]': true,
          filterLocation: selectedCityId
        }
      });

      const rawEvents = response.data?.data || [];

      // Flatten: merge publicationData fields into top level for easy access
      const allEvents = rawEvents.map(e => ({
        ...e,
        ...e.publicationData,
        _id: e._id,
        creatorProfile: e.creator
      }));

      // Check raEventId, externalLink, or any field containing ra.co
      const raEvents = allEvents.filter(e => {
        return (e.raEventId && e.raEventId.includes('ra.co'))
          || (e.externalLink && e.externalLink.includes('ra.co'));
      });

      setEvents(raEvents);
      if (raEvents.length === 0 && allEvents.length > 0) {
        message.info(`Found ${allEvents.length} events but none with RA links`);
      } else if (raEvents.length === 0) {
        message.info('No events found for this city');
      } else {
        message.success(`Loaded ${raEvents.length} events with RA links (out of ${allEvents.length} total)`);
      }
    } catch (err) {
      console.error('Error loading events:', err);
      message.error('Failed to load events: ' + (err.response?.data?.message || err.message || 'Unknown error'));
    } finally {
      setLoadingEvents(false);
    }
  };

  // ── Enrich Single Event ──
  const handleEnrichEvent = async (event) => {
    const userId = getUserId();
    if (!userId) {
      message.error('User ID not found. Please log in again.');
      return;
    }

    // RA URL can be in raEventId or externalLink
    const eventUrl = (e => {
      if (e.raEventId && e.raEventId.includes('ra.co')) return e.raEventId;
      if (e.externalLink && e.externalLink.includes('ra.co')) return e.externalLink;
      return e.raEventId || e.externalLink;
    })(event);
    const eventId = event._id;

    setEnrichingEventId(eventId);
    setEnrichResults(prev => ({
      ...prev,
      [eventId]: { status: 'enriching', results: [], summary: null }
    }));

    try {
      const response = await apiClient.post('/ai/enrich-event-artists', {
        eventUrl,
        userId,
        eventId
      });

      if (response.data?.success) {
        setEnrichResults(prev => ({
          ...prev,
          [eventId]: {
            status: 'done',
            results: response.data.results || [],
            summary: response.data.summary || {},
            event: response.data.event || {}
          }
        }));
        const s = response.data.summary || {};
        message.success(`Enriched "${event.title}": ${s.created || 0} created, ${s.existing || 0} existing, ${s.skipped || 0} skipped`);
      } else {
        throw new Error(response.data?.message || 'Failed to enrich');
      }
    } catch (err) {
      const errorMsg = err.response?.data?.message || err.message || 'Failed';
      setEnrichResults(prev => ({
        ...prev,
        [eventId]: { status: 'error', results: [], summary: null, error: errorMsg }
      }));
      message.error(`Failed to enrich "${event.title}": ${errorMsg}`);
    } finally {
      setEnrichingEventId(null);
    }
  };

  // ── Enrich All ──
  const handleEnrichAll = async () => {
    const userId = getUserId();
    if (!userId) {
      message.error('User ID not found. Please log in again.');
      return;
    }

    setBatchRunning(true);
    stopRef.current = false;

    const pending = events.filter(e => {
      const result = enrichResults[e._id];
      return !result || result.status === 'error'; // skip already done
    });

    for (let i = 0; i < pending.length; i++) {
      if (stopRef.current) {
        message.info('Batch enrichment stopped');
        break;
      }
      await handleEnrichEvent(pending[i]);

      // Longer delay to avoid RA rate-limiting (429)
      if (i < pending.length - 1 && !stopRef.current) {
        await new Promise(r => setTimeout(r, 8000));
      }
    }

    setBatchRunning(false);
  };

  const handleStopBatch = () => {
    stopRef.current = true;
  };

  // ── Fix Broken Pictures ──
  const handleFixBrokenPictures = async () => {
    setFixingPictures(true);
    setFixPicturesResult(null);

    try {
      const body = { limit: fixPicturesLimit };
      if (fixPicturesSince) {
        body.since = fixPicturesSince.format('YYYY-MM-DD');
      }
      const response = await apiClient.post('/ai/fix-broken-pictures', body);

      setFixPicturesResult(response.data);
      const fixed = response.data?.fixed || response.data?.summary?.fixed || 0;
      const total = response.data?.total || response.data?.summary?.total || 0;
      message.success(`Fixed ${fixed} out of ${total} broken pictures`);
    } catch (err) {
      const errorMsg = err.response?.data?.message || err.message || 'Failed';
      setFixPicturesResult({ error: errorMsg });
      message.error('Fix broken pictures failed: ' + errorMsg);
    } finally {
      setFixingPictures(false);
    }
  };

  // ── Stats ──
  const doneCount = events.filter(e => enrichResults[e._id]?.status === 'done').length;
  const errorCount = events.filter(e => enrichResults[e._id]?.status === 'error').length;
  const pendingCount = events.length - doneCount - errorCount;
  const progressPercent = events.length > 0 ? Math.round(((doneCount + errorCount) / events.length) * 100) : 0;

  // ── Table Columns ──
  const columns = [
    {
      title: 'Event',
      dataIndex: 'title',
      key: 'title',
      ellipsis: true,
      render: (title, record) => (
        <div>
          <strong>{title}</strong>
          {record.creatorProfile?.username && (
            <div style={{ fontSize: 12, color: '#888' }}>{record.creatorProfile.username}</div>
          )}
        </div>
      )
    },
    {
      title: 'Date',
      dataIndex: 'startTime',
      key: 'date',
      width: 120,
      render: (d) => d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '-'
    },
    {
      title: 'RA Link',
      key: 'raLink',
      width: 150,
      ellipsis: true,
      render: (_, record) => {
        const url = (record.raEventId && record.raEventId.includes('ra.co')) ? record.raEventId : record.externalLink;
        return url ? (
          <a href={url} target="_blank" rel="noopener noreferrer">
            <Tag icon={<LinkOutlined />} color="blue">{url.replace(/https?:\/\/(fr\.)?ra\.co\/events\//, 'RA #')}</Tag>
          </a>
        ) : '-';
      }
    },
    {
      title: 'Artists',
      key: 'artists',
      width: 100,
      render: (_, record) => {
        const result = enrichResults[record._id];
        if (!result || !result.summary) return <Tag>—</Tag>;
        const s = result.summary;
        return (
          <Space size={2}>
            {s.created > 0 && <Tag color="green">{s.created} new</Tag>}
            {s.existing > 0 && <Tag color="blue">{s.existing} exist</Tag>}
            {s.skipped > 0 && <Tag color="orange">{s.skipped} skip</Tag>}
            {s.failed > 0 && <Tag color="red">{s.failed} fail</Tag>}
          </Space>
        );
      }
    },
    {
      title: 'Status',
      key: 'status',
      width: 110,
      render: (_, record) => {
        const result = enrichResults[record._id];
        if (!result) return <Badge status="default" text="Pending" />;
        if (result.status === 'enriching') return <Badge status="processing" text="Enriching..." />;
        if (result.status === 'done') return <Badge status="success" text="Done" />;
        if (result.status === 'error') return <Tooltip title={result.error}><Badge status="error" text="Failed" /></Tooltip>;
        return null;
      }
    },
    {
      title: 'Action',
      key: 'action',
      width: 100,
      render: (_, record) => {
        const result = enrichResults[record._id];
        const isEnriching = enrichingEventId === record._id;
        const isDone = result?.status === 'done';

        return (
          <Button
            type={isDone ? 'default' : 'primary'}
            icon={<UserAddOutlined />}
            size="small"
            loading={isEnriching}
            disabled={batchRunning && !isEnriching}
            onClick={() => handleEnrichEvent(record)}
          >
            {isDone ? 'Re-enrich' : 'Enrich'}
          </Button>
        );
      }
    }
  ];

  // ── Expanded Row: Artist Results ──
  const expandedRowRender = (record) => {
    const result = enrichResults[record._id];
    if (!result?.results?.length) return <p style={{ color: '#999' }}>No artist results yet.</p>;

    const statusIcon = (status) => {
      if (status === 'created') return <CheckCircleOutlined style={{ color: '#52c41a' }} />;
      if (status === 'existing') return <MinusCircleOutlined style={{ color: '#1890ff' }} />;
      if (status === 'skipped') return <WarningOutlined style={{ color: '#faad14' }} />;
      if (status === 'failed') return <CloseCircleOutlined style={{ color: '#ff4d4f' }} />;
      return null;
    };

    return (
      <Table
        dataSource={result.results}
        rowKey={(r) => r.raUrl || r.name}
        size="small"
        pagination={false}
        columns={[
          {
            title: 'Artist',
            dataIndex: 'name',
            key: 'name',
            render: (name) => <strong>{name}</strong>
          },
          {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            width: 100,
            render: (status) => (
              <Space>
                {statusIcon(status)}
                <span style={{ textTransform: 'capitalize' }}>{status}</span>
              </Space>
            )
          },
          {
            title: 'Instagram',
            dataIndex: 'instagram',
            key: 'instagram',
            width: 160,
            render: (ig) => ig ? <Tag icon={<InstagramOutlined />} color="purple">{ig}</Tag> : <span style={{ color: '#ccc' }}>none</span>
          },
          {
            title: 'City',
            dataIndex: 'city',
            key: 'city',
            width: 130
          },
          {
            title: 'RA',
            dataIndex: 'raUrl',
            key: 'raUrl',
            width: 80,
            render: (url) => url ? <a href={url} target="_blank" rel="noopener noreferrer">View</a> : '-'
          }
        ]}
      />
    );
  };

  return (
    <div>
      {/* Fix Broken Pictures */}
      <Card size="small" style={{ marginBottom: 16 }}>
        <Row gutter={12} align="middle">
          <Col>
            <PictureOutlined style={{ fontSize: 18, color: '#faad14' }} />
          </Col>
          <Col>
            <strong>Fix Broken Pictures</strong>
            <div style={{ fontSize: 12, color: '#888' }}>Find profiles with expired Instagram CDN pictures, re-scrape and upload to S3</div>
          </Col>
          <Col style={{ marginLeft: 'auto' }}>
            <Space>
              <span style={{ fontSize: 12, color: '#888' }}>Since:</span>
              <DatePicker
                value={fixPicturesSince}
                onChange={setFixPicturesSince}
                size="small"
                disabled={fixingPictures}
                placeholder="All profiles"
                allowClear
              />
              <span style={{ fontSize: 12, color: '#888' }}>Limit:</span>
              <InputNumber
                min={1}
                max={500}
                value={fixPicturesLimit}
                onChange={setFixPicturesLimit}
                size="small"
                style={{ width: 70 }}
                disabled={fixingPictures}
              />
              <Button
                type="primary"
                icon={<PictureOutlined />}
                loading={fixingPictures}
                onClick={handleFixBrokenPictures}
                danger
              >
                {fixingPictures ? 'Fixing...' : 'Fix Broken Pictures'}
              </Button>
            </Space>
          </Col>
        </Row>
        {fixPicturesResult && !fixPicturesResult.error && (
          <>
            <Alert
              type={fixPicturesResult.summary?.rateLimited ? 'warning' : 'success'}
              showIcon
              style={{ marginTop: 12 }}
              message={
                <Space>
                  <span>Total: {fixPicturesResult.summary?.total ?? 0}</span>
                  <Tag color="green">Fixed: {fixPicturesResult.summary?.fixed ?? 0}</Tag>
                  <Tag color="orange">No image: {fixPicturesResult.summary?.noImage ?? 0}</Tag>
                  <Tag color="red">Failed: {fixPicturesResult.summary?.failed ?? 0}</Tag>
                  {fixPicturesResult.summary?.rateLimited && <Tag color="volcano">Rate limited — switch IP and retry</Tag>}
                </Space>
              }
            />
            {fixPicturesResult.results?.length > 0 && (
              <Table
                dataSource={fixPicturesResult.results}
                rowKey={(r, i) => r.instagram || i}
                size="small"
                pagination={false}
                scroll={{ y: 300 }}
                style={{ marginTop: 8 }}
                columns={[
                  {
                    title: 'Username',
                    dataIndex: 'username',
                    width: 160,
                    ellipsis: true,
                    render: (v) => <strong>{v}</strong>
                  },
                  {
                    title: 'Instagram',
                    dataIndex: 'instagram',
                    width: 160,
                    render: (v) => v ? <Tag icon={<InstagramOutlined />} color="purple">@{v}</Tag> : '-'
                  },
                  {
                    title: 'Status',
                    dataIndex: 'status',
                    width: 120,
                    render: (status) => {
                      const colors = { fixed: 'green', no_image: 'orange', failed: 'red', rate_limited: 'volcano' };
                      return <Tag color={colors[status] || 'default'}>{status}</Tag>;
                    }
                  },
                  {
                    title: 'Picture',
                    dataIndex: 'picture',
                    ellipsis: true,
                    render: (url) => url ? <a href={url} target="_blank" rel="noopener noreferrer">View</a> : '-'
                  }
                ]}
              />
            )}
          </>
        )}
        {fixPicturesResult?.error && (
          <Alert type="error" showIcon style={{ marginTop: 12 }} message={fixPicturesResult.error} />
        )}
      </Card>

      <Divider />

      <Alert
        message="Enrich Event Artists"
        description="Select a city, load its events, then enrich each event to auto-create artist profiles from the RA lineup. Artists without Instagram on RA are skipped."
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
      />

      {/* City Selector */}
      <Row gutter={12} style={{ marginBottom: 16 }} align="middle">
        <Col>
          <Select
            placeholder="Country"
            style={{ width: 200 }}
            showSearch
            optionFilterProp="children"
            value={selectedCountryId}
            onChange={handleCountryChange}
          >
            {activeCountries.map(c => (
              <Select.Option key={c._id} value={c._id}>{c.name}</Select.Option>
            ))}
          </Select>
        </Col>
        <Col>
          <Select
            placeholder="City"
            style={{ width: 200 }}
            showSearch
            optionFilterProp="children"
            disabled={!selectedCountryId}
            value={selectedCityId}
            onChange={handleCityChange}
          >
            {cities.map(c => (
              <Select.Option key={c._id} value={c._id}>{c.name}</Select.Option>
            ))}
          </Select>
        </Col>
        <Col>
          <Button
            type="primary"
            icon={<SearchOutlined />}
            onClick={handleLoadEvents}
            loading={loadingEvents}
            disabled={!selectedCityId}
          >
            Load Events
          </Button>
        </Col>
      </Row>

      {/* Events Table */}
      {events.length > 0 && (
        <>
          {/* Progress + Batch Controls */}
          <Row gutter={16} align="middle" style={{ marginBottom: 12 }}>
            <Col flex="auto">
              <Progress percent={progressPercent} size="small" />
              <Space style={{ marginTop: 4 }}>
                <Badge status="default" text={`${pendingCount} pending`} />
                <Badge status="success" text={`${doneCount} done`} />
                <Badge status="error" text={`${errorCount} failed`} />
              </Space>
            </Col>
            <Col>
              {!batchRunning ? (
                <Button
                  type="primary"
                  icon={<ThunderboltOutlined />}
                  onClick={handleEnrichAll}
                  disabled={pendingCount === 0}
                >
                  Enrich All ({pendingCount})
                </Button>
              ) : (
                <Button
                  danger
                  icon={<PauseCircleOutlined />}
                  onClick={handleStopBatch}
                >
                  Stop
                </Button>
              )}
            </Col>
          </Row>

          <Table
            dataSource={events}
            columns={columns}
            rowKey="_id"
            size="small"
            pagination={{ pageSize: 50 }}
            expandable={{
              expandedRowRender,
              rowExpandable: (record) => !!enrichResults[record._id]?.results?.length
            }}
          />
        </>
      )}

      {/* Empty state */}
      {!loadingEvents && events.length === 0 && selectedCityId && (
        <Alert
          message="No events loaded"
          description="Click 'Load Events' to fetch events for the selected city. Only events with RA links will be shown."
          type="warning"
          showIcon
        />
      )}
    </div>
  );
};

export default EnrichArtistTab;
